/* eslint-disable no-useless-catch */
import {apiClient} from './apiClient';
import {UserSearchRequest, UserSearchResult, Recommendation} from '../types/api';
import settings from '../settings';

/**
 * Admin service for handling admin-specific API calls
 * Following the service layer pattern from best practices guide
 */
export class AdminService {
    /**
     * Search for users in the system
     */
    static async searchUsers(searchCriteria: UserSearchRequest): Promise<UserSearchResult[]> {
        try {
            return await apiClient.post<UserSearchResult[]>('/api/users', searchCriteria);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get detailed user information by ID
     */
    static async getUserById(userId: number): Promise<any> {
        try {
            return await apiClient.get<any>(`/api/admin/users/${userId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update user information
     */
    static async updateUser(userInfo: any): Promise<any> {
        try {
            return await apiClient.post<any>('/api/user', userInfo);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete user account
     */
    static async deleteUser(userId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(`/api/admin/users/${userId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Reset user password (admin action) - matches old format
     */
    static async resetUserPassword(email: string, newPassword: string): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/admin/reset', {
                username: email, // Old version uses 'username'
                password: newPassword,
                ref: 'admin', // Old version includes 'ref'
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Impersonate user (admin action)
     * Returns login token for the specified user
     */
    static async impersonateUser(email: string): Promise<{result: any}> {
        try {
            return await apiClient.post<{result: any}>('/api/impersonate', {
                username: email,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Exit user impersonation (admin action)
     * Returns to original admin user
     */
    static async exitImpersonation(originalAdminToken?: string): Promise<{result: any}> {
        try {
            if (originalAdminToken) {
                // Use direct fetch call with original admin token (matches old implementation)
                const response = await fetch(`${settings.apiURL}/api/impersonate`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${originalAdminToken}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                return await response.json();
            } else {
                // Fallback to apiClient (will likely fail with expired token)
                return await apiClient.delete<{result: any}>('/api/impersonate');
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get all system recommendations
     */
    static async getAllRecommendations(): Promise<Recommendation[]> {
        try {
            return await apiClient.get<Recommendation[]>('/api/recommendations');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get single recommendation by ID
     */
    static async getRecommendation(recommendationId: number): Promise<Recommendation> {
        try {
            return await apiClient.get<Recommendation>(`/api/recommendations/${recommendationId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create new recommendation
     */
    static async createRecommendation(recommendation: Omit<Recommendation, 'Id'>): Promise<Recommendation> {
        try {
            return await apiClient.post<Recommendation>('/api/recommendations', recommendation);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update existing recommendation
     */
    static async updateRecommendation(recommendationId: number, updates: Omit<Recommendation, 'Id'>): Promise<any> {
        try {
            const result = await apiClient.put<any>(`/api/recommendations/${recommendationId}`, updates);
            return result;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete recommendation
     */
    static async deleteRecommendation(recommendationId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(`/api/recommendations/${recommendationId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search orders
     */
    static async searchOrders(searchFields: any): Promise<any[]> {
        try {
            return await apiClient.post<any[]>('/api/order/search', searchFields);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get order details
     */
    static async getOrder(orderId: number): Promise<any> {
        try {
            return await apiClient.get<any>(`/api/admin/orders/${orderId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update order status
     */
    static async updateOrderStatus(orderId: number, status: string): Promise<{success: boolean}> {
        try {
            return await apiClient.patch<{success: boolean}>(`/api/admin/orders/${orderId}/status`, {status});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search photos/media - using direct axios to match old implementation
     */
    static async searchPhotos(email: string): Promise<any> {
        try {
            const axios = (await import('axios')).default;

            // Get token from localStorage like old implementation
            const token = localStorage.getItem('authToken') || localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios(`${settings.apiURL}/api/claim`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                data: JSON.stringify({email}),
            });

            if (response.status !== 200) {
                throw new Error('Could not get photos');
            }

            return response.data;
        } catch (error) {
            console.error('Photo search error:', error);
            throw error;
        }
    }

    /**
     * Search kit videos by kit ID (matches old VideoSearch)
     */
    static async searchKitVideos(kitId: string): Promise<any> {
        try {
            return await apiClient.post<any>('/api/kit/getKit', {kit_id: kitId});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get recommendations (alias for getAllRecommendations)
     */
    static async getRecommendations(): Promise<any[]> {
        try {
            const result = await this.getAllRecommendations();
            return result;
        } catch (error) {
            console.error('Get recommendations error:', error);
            throw error;
        }
    }

    /**
     * Add recommendation - now receives correct format from new component
     */
    static async addRecommendation(recommendation: any): Promise<any> {
        try {
            // Component now sends correct format directly - no transformation needed
            const result = await apiClient.post<any>('/api/recommendations', recommendation);
            return result;
        } catch (error) {
            console.error('Add recommendation error:', error);
            throw error;
        }
    }

    /**
     * Upload media file
     */
    static async uploadMedia(file: File, onProgress?: (progress: number) => void): Promise<{url: string}> {
        try {
            return await apiClient.uploadFile<{url: string}>('/api/admin/media/upload', file, onProgress);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete media file
     */
    static async deleteMedia(mediaId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(`/api/admin/media/${mediaId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search videos
     */
    static async searchVideos(searchTerm: string): Promise<any[]> {
        try {
            return await apiClient.post<any[]>('/api/admin/videos/search', {search: searchTerm});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get rejected emails
     */
    static async getRejectedEmails(startDatetime?: number, endDatetime?: number): Promise<any[]> {
        try {
            const params = new URLSearchParams();
            if (startDatetime) params.append('startDatetime', startDatetime.toString());
            if (endDatetime) params.append('endDatetime', endDatetime.toString());

            const queryString = params.toString();
            const url = queryString ? `/api/email/rejected?${queryString}` : '/api/email/rejected';

            return await apiClient.get<any[]>(url);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Resend rejected email
     */
    static async resendRejectedEmail(emailId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>(`/api/admin/emails/${emailId}/resend`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Add new test to the system (matches old AddTest)
     */
    static async addTest(testData: any): Promise<any> {
        try {
            return await apiClient.post<any>('/api/saveProduct', testData);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update test information
     */
    static async updateTest(testId: number, updates: any): Promise<any> {
        try {
            return await apiClient.patch<any>(`/api/admin/tests/${testId}`, updates);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get system statistics
     */
    static async getSystemStats(): Promise<any> {
        try {
            return await apiClient.get<any>('/api/admin/stats');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get audit logs
     */
    static async getAuditLogs(limit?: number, offset?: number): Promise<any[]> {
        try {
            const params = new URLSearchParams();
            if (limit) params.append('limit', limit.toString());
            if (offset) params.append('offset', offset.toString());

            const queryString = params.toString();
            const url = queryString ? `/api/admin/audit-logs?${queryString}` : '/api/admin/audit-logs';

            return await apiClient.get<any[]>(url);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Export user data (admin action)
     */
    static async exportUserData(userId: number): Promise<Blob> {
        try {
            return await apiClient.get(`/api/admin/users/${userId}/export`, {
                responseType: 'blob',
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Bulk export system data
     */
    static async bulkExportData(dataType: string, filters?: any): Promise<Blob> {
        try {
            return await apiClient.post('/api/admin/export/bulk', {dataType, filters}, {responseType: 'blob'});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Send system-wide notification
     */
    static async sendSystemNotification(notification: {
        title: string;
        message: string;
        userIds?: number[];
        sendToAll?: boolean;
    }): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/admin/notifications', notification);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Search biomarkers by name with fuzzy matching
     */
    static async searchBiomarkers(searchTerm: string): Promise<string[]> {
        try {
            return await apiClient.get<string[]>(`/api/biomarkers/search?search=${encodeURIComponent(searchTerm)}`);
        } catch (error) {
            console.error('Biomarker search error:', error);
            throw error;
        }
    }
}

export default AdminService;
